/**
 * Global Products Initialization Composable
 * 
 * This composable manages the initialization of all products for the entire site.
 * It runs from the default layout and ensures products are fetched only once.
 * After initialization, all product operations work from the store.
 */

// Global state to ensure initialization only happens once
let globalInitStarted = false
let globalInitPromise: Promise<void> | null = null

export const useGlobalProductsInit = () => {
  const route = useRoute()
  const productsStore = useProductsStore()
  
  // State to track if initialization is in progress
  const isInitializing = ref(false)
  const initError = ref<string | null>(null)
  
  /**
   * Initialize all products and categories
   * This is the main entry point called from the layout
   */
  const initializeProducts = async () => {
    // If already fully initialized, skip
    if (productsStore.isFullyInitialized) {
      console.log('[GlobalProductsInit] Products already initialized, skipping...')
      return
    }
    
    // Use global flag to prevent multiple simultaneous initializations
    if (globalInitStarted) {
      console.log('[GlobalProductsInit] Global initialization already started by another instance')
      if (globalInitPromise) {
        await globalInitPromise
      }
      return
    }
    
    // Mark global init as started
    globalInitStarted = true
    
    // Create the initialization promise
    globalInitPromise = (async () => {
      try {
        isInitializing.value = true
        initError.value = null
        
        console.log('[GlobalProductsInit] Starting global products initialization...')
        console.log('[GlobalProductsInit] Current route:', route.path)
        
        // First, ensure categories are loaded
        if (!productsStore.categoriesInitialized) {
          console.log('[GlobalProductsInit] Loading categories...')
          await productsStore.fetchCategories()
        }
        
        // Fetch ALL products in one go (as requested)
        console.log('[GlobalProductsInit] Fetching all products...')
        await productsStore.fetchAllProducts()
        
        console.log('[GlobalProductsInit] Initialization complete!')
        console.log(`[GlobalProductsInit] Total products loaded: ${productsStore.productList.length}`)
        console.log(`[GlobalProductsInit] Total categories loaded: ${productsStore.flatCategories.length}`)
        
      } catch (error) {
        console.error('[GlobalProductsInit] Error during initialization:', error)
        initError.value = error instanceof Error ? error.message : 'Failed to initialize products'
        // Reset global flag on error so it can be retried
        globalInitStarted = false
        throw error
      } finally {
        isInitializing.value = false
      }
    })()
    
    await globalInitPromise
  }
  
  /**
   * Wait for products to be initialized
   * Useful for pages that need to ensure products are loaded
   */
  const waitForInitialization = async () => {
    // If already initialized, return immediately
    if (productsStore.isFullyInitialized) {
      return true
    }
    
    // If not initializing, start initialization
    if (!isInitializing.value) {
      await initializeProducts()
      return productsStore.isFullyInitialized
    }
    
    // Wait for ongoing initialization to complete
    return new Promise<boolean>((resolve) => {
      const checkInterval = setInterval(() => {
        if (!isInitializing.value) {
          clearInterval(checkInterval)
          resolve(productsStore.isFullyInitialized)
        }
      }, 100)
      
      // Timeout after 30 seconds
      setTimeout(() => {
        clearInterval(checkInterval)
        resolve(false)
      }, 30000)
    })
  }
  
  /**
   * Get initialization status
   */
  const getStatus = () => ({
    isInitializing: isInitializing.value,
    isFullyInitialized: productsStore.isFullyInitialized,
    productsCount: productsStore.productList.length,
    categoriesCount: productsStore.flatCategories.length,
    error: initError.value
  })
  
  // Don't auto-initialize - let the layout control when to initialize
  
  return {
    initializeProducts,
    waitForInitialization,
    getStatus,
    isInitializing: readonly(isInitializing),
    initError: readonly(initError),
    isFullyInitialized: computed(() => productsStore.isFullyInitialized)
  }
}